import React, { useState, useEffect, useRef } from 'react';
import { 
  X, ShieldAlert, Lock, BookOpen, ChevronLeft, ChevronRight, 
  ZoomIn, ZoomOut, EyeOff, Award
} from 'lucide-react';

interface Chapter {
  title: string;
  dest?: string;
  chapterNumber?: number;
  content?: string;
}

interface PublicationReaderProps {
  publication: {
    id?: string;
    _id?: string;
    title: string;
    subtitle?: string;
    authors?: string[];
    category?: string;
    pageCount?: number;
    outline?: any[];
    chapters?: Chapter[];
    fullText?: string;
    watermarkText?: string;
  };
  userEmail?: string;
  userName?: string;
  onClose: () => void;
}

const SecurePublicationReader: React.FC<PublicationReaderProps> = ({
  publication,
  userEmail = 'scholar@elumlaw.com',
  userName = 'Valued Legal Scholar',
  onClose
}) => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [fontSize, setFontSize] = useState<number>(16);
  const [readerTheme, setReaderTheme] = useState<'dark' | 'sepia' | 'light'>('dark');
  const [screenBlurred, setScreenBlurred] = useState(false);
  const [drmWarning, setDrmWarning] = useState<string | null>(null);
  const readerContainerRef = useRef<HTMLDivElement | null>(null);

  const chapters: Chapter[] = (publication.chapters && publication.chapters.length > 0)
    ? publication.chapters
    : (publication.outline && publication.outline.length > 0)
      ? publication.outline.map((o, idx) => ({ title: o.title || `Chapter ${idx + 1}`, dest: o.dest }))
      : [{ title: 'Full Publication', content: publication.fullText }];

  const currentChapter = chapters[currentChapterIndex] || chapters[0] || { title: publication.title };

  const triggerDrmWarning = (msg: string) => {
    setDrmWarning(msg);
    setTimeout(() => {
      setDrmWarning(null);
    }, 4500);
  };

  // 1. Strict Anti-Print and Anti-Screenshot Keyboard & Clipboard Listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Intercept Ctrl+P, Cmd+P (Print)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        e.stopPropagation();
        triggerDrmWarning('Printing is strictly prohibited for this digital publication under Intellectual Property Rights.');
        return false;
      }
      // Intercept Ctrl+S, Cmd+S (Save)
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        e.stopPropagation();
        triggerDrmWarning('Saving or exporting page source is prohibited.');
        return false;
      }
      // Intercept Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Intercept F12 or Developer tools
      if (e.key === 'F12' || ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'C' || e.key === 'J'))) {
        e.preventDefault();
        e.stopPropagation();
        triggerDrmWarning('Developer inspection tools are disabled in DRM protected mode.');
        return false;
      }
      // Intercept PrintScreen key
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        setScreenBlurred(true);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText('');
        }
        triggerDrmWarning('Screen capture attempted: Clipboard has been sanitized.');
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText('');
        }
        setScreenBlurred(true);
      }
    };

    const handleCopyCut = (e: ClipboardEvent) => {
      e.preventDefault();
      triggerDrmWarning('Text copying and extraction is disabled on this copyrighted monograph.');
      if (e.clipboardData) {
        e.clipboardData.setData('text/plain', 'Protected by Elum Law DRM Shield. Direct copying prohibited.');
      }
      return false;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setScreenBlurred(true);
      }
    };

    const handleWindowBlur = () => {
      setScreenBlurred(true);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('copy', handleCopyCut, true);
    document.addEventListener('cut', handleCopyCut, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('copy', handleCopyCut, true);
      document.removeEventListener('cut', handleCopyCut, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const themeStyles = {
    dark: {
      bg: '#090d16',
      cardBg: '#0f172a',
      text: '#e2e8f0',
      border: '#1e293b',
      accent: '#ecc94b',
      sidebarBg: '#0b1120',
      headerBg: '#0a0f1d'
    },
    sepia: {
      bg: '#fbf0d9',
      cardBg: '#f4ecd8',
      text: '#433422',
      border: '#e4d5b7',
      accent: '#b45309',
      sidebarBg: '#ebdcc2',
      headerBg: '#f1e2c9'
    },
    light: {
      bg: '#f1f5f9',
      cardBg: '#ffffff',
      text: '#0f172a',
      border: '#e2e8f0',
      accent: '#2563eb',
      sidebarBg: '#f8fafc',
      headerBg: '#ffffff'
    }
  }[readerTheme];

  const getChapterContent = () => {
    let raw = '';
    if (currentChapter.content && currentChapter.content.trim().length > 10) {
      raw = currentChapter.content;
    } else if (publication.fullText) {
      const titleSearch = currentChapter.title.trim();
      const pos = publication.fullText.indexOf(titleSearch);
      if (pos !== -1) {
        raw = publication.fullText.substring(pos, pos + 30000);
      } else {
        raw = publication.fullText.slice(0, 30000);
      }
    }

    if (!raw) {
      return ['Digital publication content encrypted and secured. Chapter text loaded.'];
    }

    const rawParagraphs = raw.split(/\r?\n\r?\n/).map((p: string) => p.trim()).filter(Boolean);
    if (rawParagraphs.length <= 1) {
      return raw.split(/\r?\n/).map((p: string) => p.trim()).filter((p: string) => p.length > 0);
    }
    return rawParagraphs;
  };

  const paragraphs = getChapterContent();

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: themeStyles.bg,
        color: themeStyles.text,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        triggerDrmWarning('Right-click context menu is disabled to prevent unauthorized extraction.');
      }}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Dynamic Print Disabler */}
      <style>{`
        @media print {
          body * { display: none !important; }
          body::after {
            content: "PRINTING STRICTLY PROHIBITED. This publication is protected by Elum Law Digital Rights Management (DRM). Unauthorized reproduction or physical printing is punishable under Intellectual Property Law.";
            display: block !important;
            padding: 40px;
            font-size: 20px;
            color: red;
            font-weight: bold;
            text-align: center;
          }
        }
      `}</style>

      {/* TOP HEADER CONTROLS BAR */}
      <div style={{
        height: '64px',
        backgroundColor: themeStyles.headerBg,
        borderBottom: `2px solid ${readerTheme === 'dark' ? '#ecc94b' : themeStyles.border}`,
        padding: '0 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 50,
        flexShrink: 0,
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        boxSizing: 'border-box'
      }}>
        {/* LEFT: BOOK INFO & DRM BADGE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden', maxWidth: '60%' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: 'rgba(236,201,75,0.15)',
            border: '1.5px solid #ecc94b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Lock size={18} color="#ecc94b" />
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <h2 style={{
                fontSize: '1rem',
                fontWeight: '900',
                color: themeStyles.text,
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {publication.title}
              </h2>
              <span style={{
                backgroundColor: 'rgba(239,68,68,0.2)',
                color: '#f87171',
                border: '1px solid rgba(239,68,68,0.4)',
                fontSize: '0.65rem',
                fontWeight: '900',
                padding: '0.15rem 0.5rem',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                DRM PROTECTED • READ-ONLY
              </span>
            </div>
            <p style={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              margin: '2px 0 0 0',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentChapter.title} • Section {currentChapterIndex + 1} of {chapters.length}
            </p>
          </div>
        </div>

        {/* RIGHT: CONTROLS (FONT, THEME, CLOSE) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          {/* FONT RESIZE */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
            borderRadius: '10px',
            padding: '0.2rem 0.4rem',
            border: `1px solid ${themeStyles.border}`
          }}>
            <button
              onClick={() => setFontSize(prev => Math.max(12, prev - 2))}
              style={{
                background: 'none',
                border: 'none',
                color: themeStyles.text,
                cursor: 'pointer',
                padding: '0.3rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Decrease Font Size"
            >
              <ZoomOut size={16} />
            </button>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '800',
              fontFamily: 'monospace',
              color: themeStyles.accent,
              padding: '0 0.35rem'
            }}>
              {fontSize}px
            </span>
            <button
              onClick={() => setFontSize(prev => Math.min(28, prev + 2))}
              style={{
                background: 'none',
                border: 'none',
                color: themeStyles.text,
                cursor: 'pointer',
                padding: '0.3rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Increase Font Size"
            >
              <ZoomIn size={16} />
            </button>
          </div>

          {/* THEME TOGGLE */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
            borderRadius: '10px',
            padding: '0.2rem',
            border: `1px solid ${themeStyles.border}`,
            gap: '0.2rem'
          }}>
            <button
              onClick={() => setReaderTheme('dark')}
              style={{
                backgroundColor: readerTheme === 'dark' ? '#090d16' : 'transparent',
                border: readerTheme === 'dark' ? '1px solid #ecc94b' : 'none',
                borderRadius: '8px',
                padding: '0.3rem 0.5rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Dark Theme"
            >
              🌙
            </button>
            <button
              onClick={() => setReaderTheme('sepia')}
              style={{
                backgroundColor: readerTheme === 'sepia' ? '#f4ecd8' : 'transparent',
                border: readerTheme === 'sepia' ? '1px solid #b45309' : 'none',
                borderRadius: '8px',
                padding: '0.3rem 0.5rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Sepia Theme"
            >
              📜
            </button>
            <button
              onClick={() => setReaderTheme('light')}
              style={{
                backgroundColor: readerTheme === 'light' ? '#ffffff' : 'transparent',
                border: readerTheme === 'light' ? '1px solid #2563eb' : 'none',
                borderRadius: '8px',
                padding: '0.3rem 0.5rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Light Theme"
            >
              ☀️
            </button>
          </div>

          {/* EXIT / CLOSE BUTTON */}
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '0.5rem 0.9rem',
              fontWeight: '800',
              fontSize: '0.82rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              boxShadow: '0 2px 8px rgba(239,68,68,0.5)'
            }}
            title="Exit Reader"
          >
            <X size={16} />
            <span>Close</span>
          </button>
        </div>
      </div>

      {/* ALERT BANNER IF DRM VIOLATION TRIGGERED */}
      {drmWarning && (
        <div style={{
          backgroundColor: '#dc2626',
          color: 'white',
          padding: '0.65rem 1.25rem',
          fontSize: '0.85rem',
          fontWeight: '800',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 60,
          boxShadow: '0 4px 12px rgba(220,38,38,0.5)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ShieldAlert size={18} color="white" />
            <span>{drmWarning}</span>
          </div>
          <button
            onClick={() => setDrmWarning(null)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* MAIN READER BODY WITH TABLE OF CONTENTS + READING CANVAS */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        
        {/* CHAPTERS NAVIGATION SIDEBAR */}
        <div style={{
          width: '300px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: themeStyles.sidebarBg,
          borderRight: `1px solid ${themeStyles.border}`,
          flexShrink: 0,
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}>
          <div style={{
            padding: '1rem',
            borderBottom: `1px solid ${themeStyles.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: '900',
              textTransform: 'uppercase',
              color: themeStyles.accent,
              letterSpacing: '0.8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}>
              <BookOpen size={16} color={themeStyles.accent} />
              Table of Contents
            </span>
            <span style={{
              fontSize: '0.72rem',
              fontWeight: '800',
              padding: '0.2rem 0.5rem',
              borderRadius: '6px',
              backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
              color: '#94a3b8'
            }}>
              {chapters.length} Sections
            </span>
          </div>

          <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {chapters.map((chap, idx) => {
              const isActive = currentChapterIndex === idx;
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentChapterIndex(idx);
                    if (readerContainerRef.current) readerContainerRef.current.scrollTop = 0;
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.75rem 0.85rem',
                    borderRadius: '10px',
                    fontSize: '0.82rem',
                    fontWeight: isActive ? '900' : '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.6rem',
                    backgroundColor: isActive 
                      ? (readerTheme === 'dark' ? 'rgba(236,201,75,0.15)' : 'rgba(37,99,235,0.15)')
                      : 'transparent',
                    color: isActive ? themeStyles.accent : themeStyles.text,
                    border: isActive ? `1.5px solid ${themeStyles.accent}` : '1px solid transparent',
                    boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                    transition: 'all 0.15s ease',
                    boxSizing: 'border-box'
                  }}
                >
                  <span style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? themeStyles.accent : (readerTheme === 'dark' ? '#1e293b' : '#e2e8f0'),
                    color: isActive ? '#0f172a' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.72rem',
                    fontWeight: '900',
                    flexShrink: 0,
                    marginTop: '1px'
                  }}>
                    {idx + 1}
                  </span>
                  <span style={{ lineHeight: '1.3', flex: 1 }}>{chap.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* READING CANVAS (WATERMARKED & PROTECTED) */}
        <div 
          ref={readerContainerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '2.5rem 1.5rem',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: themeStyles.bg,
            boxSizing: 'border-box'
          }}
        >
          {/* SEMI-TRANSPARENT DIAGONAL REPEATING WATERMARK OVERLAY */}
          <div 
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 20,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-around',
              opacity: 0.07,
              overflow: 'hidden',
              userSelect: 'none',
              transform: 'rotate(-25deg) scale(1.3)'
            }}
          >
            {Array.from({ length: 40 }).map((_, i) => (
              <div key={i} style={{
                fontSize: '0.85rem',
                fontWeight: '900',
                letterSpacing: '2px',
                color: themeStyles.text,
                padding: '2.5rem',
                textAlign: 'center',
                textTransform: 'uppercase'
              }}>
                ELUM LAW DIGITAL ARCHIVE • {userEmail} • DRM VERIFIED
              </div>
            ))}
          </div>

          {/* BOOK PAGE CONTAINER */}
          <div 
            style={{ 
              width: '100%',
              maxWidth: '860px',
              borderRadius: '20px',
              padding: '2.5rem',
              boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
              position: 'relative',
              zIndex: 10,
              backgroundColor: themeStyles.cardBg, 
              color: themeStyles.text, 
              border: `1.5px solid ${themeStyles.border}`,
              boxSizing: 'border-box',
              height: 'fit-content'
            }}
          >
            {/* CHAPTER HEADER */}
            <div style={{
              borderBottom: `2px solid ${themeStyles.border}`,
              paddingBottom: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '900',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  padding: '0.25rem 0.65rem',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(236,201,75,0.15)',
                  color: themeStyles.accent,
                  border: `1px solid ${themeStyles.accent}`
                }}>
                  Section {currentChapterIndex + 1} of {chapters.length}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  • {publication.category || 'Law Publication & Solved Papers'}
                </span>
              </div>

              <h1 style={{
                fontSize: '1.85rem',
                fontWeight: '900',
                lineHeight: '1.3',
                margin: '0 0 0.5rem 0'
              }}>
                {currentChapter.title}
              </h1>

              {publication.authors && publication.authors.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.85rem', fontWeight: '700' }}>
                  <Award size={16} color={themeStyles.accent} />
                  <span>By {publication.authors.join(' & ')}</span>
                </div>
              )}
            </div>

            {/* READING CONTENT PARAGRAPHS */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
              lineHeight: '1.75',
              textAlign: 'justify',
              fontSize: `${fontSize}px`
            }}>
              {paragraphs.map((para, pIdx) => {
                const isHeading = /^(CSS Examination|Question \d+|1\.\d+|2\.\d+|Surah |Translation|Scholarly Exegesis|Prophetic Precedent)/i.test(para);
                const isArabic = /[\u0600-\u06FF]/.test(para) && para.length < 500;

                if (isArabic) {
                  return (
                    <div 
                      key={pIdx} 
                      style={{
                        margin: '1.5rem 0',
                        padding: '1rem 1.5rem',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(236,201,75,0.1)',
                        border: '1.5px solid #ecc94b',
                        textAlign: 'right',
                        fontFamily: '"Traditional Arabic", "Scheherazade", serif',
                        fontSize: `${fontSize + 4}px`,
                        lineHeight: '2.2',
                        color: themeStyles.accent,
                        direction: 'rtl'
                      }}
                    >
                      {para}
                    </div>
                  );
                }

                if (isHeading) {
                  return (
                    <h3 
                      key={pIdx} 
                      style={{
                        fontSize: `${fontSize + 3}px`,
                        fontWeight: '900',
                        color: themeStyles.accent,
                        marginTop: '1.5rem',
                        marginBottom: '0.25rem',
                        borderBottom: `1px solid ${themeStyles.border}`,
                        paddingBottom: '0.4rem'
                      }}
                    >
                      {para}
                    </h3>
                  );
                }

                return (
                  <p 
                    key={pIdx} 
                    style={{
                      margin: 0,
                      textIndent: '1.5rem',
                      lineHeight: '1.75'
                    }}
                  >
                    {para}
                  </p>
                );
              })}
            </div>

            {/* CHAPTER PAGINATION BOTTOM */}
            <div style={{
              marginTop: '3rem',
              paddingTop: '1.5rem',
              borderTop: `2px solid ${themeStyles.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <button
                disabled={currentChapterIndex === 0}
                onClick={() => {
                  setCurrentChapterIndex(prev => Math.max(0, prev - 1));
                  if (readerContainerRef.current) readerContainerRef.current.scrollTop = 0;
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.65rem 1.25rem',
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
                  color: themeStyles.text,
                  border: `1px solid ${themeStyles.border}`,
                  cursor: currentChapterIndex === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentChapterIndex === 0 ? 0.35 : 1
                }}
              >
                <ChevronLeft size={16} />
                <span>Previous Section</span>
              </button>

              <span style={{
                fontSize: '0.85rem',
                fontFamily: 'monospace',
                color: '#94a3b8',
                fontWeight: '700'
              }}>
                Section {currentChapterIndex + 1} of {chapters.length}
              </span>

              <button
                disabled={currentChapterIndex === chapters.length - 1}
                onClick={() => {
                  setCurrentChapterIndex(prev => Math.min(chapters.length - 1, prev + 1));
                  if (readerContainerRef.current) readerContainerRef.current.scrollTop = 0;
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.65rem 1.25rem',
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: '900',
                  backgroundColor: themeStyles.accent,
                  color: '#0f172a',
                  border: 'none',
                  cursor: currentChapterIndex === chapters.length - 1 ? 'not-allowed' : 'pointer',
                  opacity: currentChapterIndex === chapters.length - 1 ? 0.35 : 1,
                  boxShadow: '0 2px 10px rgba(236,201,75,0.4)'
                }}
              >
                <span>Next Section</span>
                <ChevronRight size={16} />
              </button>
            </div>

          </div>
        </div>

      </div>

      {/* SCREEN CAPTURE & RECORDING SHIELD (ACTIVATES WHEN WINDOW BLURS) */}
      {screenBlurred && (
        <div 
          onClick={() => setScreenBlurred(false)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 200000,
            backgroundColor: 'rgba(9, 13, 22, 0.97)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '2rem',
            cursor: 'pointer'
          }}
        >
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(239,68,68,0.2)',
            border: '2px solid #ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1.5rem'
          }}>
            <EyeOff size={36} color="#ef4444" />
          </div>
          <h2 style={{
            fontSize: '1.65rem',
            fontWeight: '900',
            color: '#ffffff',
            margin: '0 0 0.75rem 0'
          }}>
            🔒 Content Shielded for Intellectual Property Security
          </h2>
          <p style={{
            fontSize: '0.9rem',
            color: '#cbd5e1',
            maxWidth: '520px',
            lineHeight: '1.6',
            margin: '0 0 1.5rem 0'
          }}>
            Screen capture, background recording, or external window switching was detected. Elum Law Digital Rights Management has shielded this publication to prevent unauthorized capture.
          </p>
          <button
            onClick={() => setScreenBlurred(false)}
            style={{
              padding: '0.75rem 2rem',
              borderRadius: '12px',
              backgroundColor: '#ecc94b',
              color: '#0f172a',
              fontWeight: '900',
              fontSize: '0.95rem',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(236,201,75,0.4)'
            }}
          >
            Click to Resume Reading
          </button>
        </div>
      )}

      {/* FOOTER DRM STATUS */}
      <div style={{
        height: '32px',
        backgroundColor: '#070b14',
        borderTop: `1px solid ${themeStyles.border}`,
        padding: '0 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.72rem',
        color: '#64748b',
        flexShrink: 0,
        fontFamily: 'monospace'
      }}>
        <span>ELUM LAW ASSOCIATES • DIGITAL DRM VAULT</span>
        <span>PROTECTED USER: {userEmail} • ACTIVE SESSION</span>
        <span>COPYRIGHT © 2026 ALL RIGHTS RESERVED</span>
      </div>

    </div>
  );
};

export default SecurePublicationReader;
